import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Wrench, Cog } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Field } from "./Companies";

const CATEGORIES = ["storage", "dosing", "mixing", "conveying", "preparation", "service"];

function fmtMoney(v) {
  if (!v) return "—";
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}k`;
  return `$${v}`;
}

function emptyForm() {
  return {
    name: "", kind: "equipment", category: "storage",
    description: "", applications: [], typical_price_usd: 0,
  };
}

export default function Catalog() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [appsText, setAppsText] = useState("");

  const load = async () => {
    const r = await api.get("/products");
    setItems(r.data);
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter((i) => {
    if (tab === "all") return true;
    if (tab === "service") return i.kind === "service";
    if (tab === "equipment") return i.kind === "equipment";
    return i.category === tab;
  });

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setAppsText("");
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...emptyForm(), ...item });
    setAppsText((item.applications || []).join(", "));
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      payload.typical_price_usd = parseFloat(payload.typical_price_usd || 0);
      payload.applications = appsText.split(",").map((s) => s.trim()).filter(Boolean);
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Updated" : "Created");
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const tabs = [
    { id: "all", label: "All" },
    { id: "equipment", label: "Equipment" },
    { id: "service", label: "Services" },
  ];

  return (
    <div data-testid="catalog-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-eyebrow">Sodimate</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Catalog</h1>
          <p className="text-sm text-gray-500 mt-2">{filtered.length} products &amp; services</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="add-product-button">
          <Plus size={16} /> New Product
        </button>
      </div>

      <div className="border-b border-gray-200 mb-6 flex gap-6">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`catalog-tab-${t.id}`}
            className={`pb-3 text-sm font-semibold transition-colors border-b-2 ${
              tab === t.id ? "text-[#004B87] border-[#004B87]" : "text-gray-500 border-transparent hover:text-gray-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((p) => (
          <div key={p.id} className="section-card p-5 group" data-testid={`product-card-${p.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 grid place-items-center bg-[#004B87]/5 border border-[#004B87]/20">
                {p.kind === "service" ? (
                  <Wrench size={16} className="text-[#004B87]" />
                ) : (
                  <Cog size={16} className="text-[#004B87]" />
                )}
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => openEdit(p)} className="text-gray-400 hover:text-[#004B87] p-1">
                  <Pencil size={14} />
                </button>
                <button onClick={() => remove(p.id)} className="text-gray-400 hover:text-red-600 p-1">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className="label-eyebrow">{p.category}</div>
            <h3 className="font-display text-lg font-bold tracking-tight mt-1 mb-2">{p.name}</h3>
            <p className="text-sm text-gray-600 line-clamp-3 mb-4">{p.description}</p>
            <div className="flex items-end justify-between pt-3 border-t border-gray-100">
              <div>
                <div className="label-eyebrow">Indicative</div>
                <div className="font-display text-base font-bold">{fmtMoney(p.typical_price_usd)}</div>
              </div>
              <span className={`chip ${p.kind === "service" ? "border-[#7C3AED] text-[#7C3AED] bg-purple-50" : "border-[#004B87] text-[#004B87] bg-blue-50"}`}>
                {p.kind}
              </span>
            </div>
            {p.applications?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {p.applications.slice(0, 3).map((a) => (
                  <span key={a} className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{a}</span>
                )).reduce((prev, curr, i) => i === 0 ? [curr] : [...prev, <span key={`s${i}`} className="text-gray-300 text-[10px]">·</span>, curr], [])}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 text-center text-gray-400 py-16">No products</div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setShowModal(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={save}
            data-testid="product-form-modal"
            className="bg-white border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="label-eyebrow">{editing ? "Edit" : "New"}</div>
                <div className="font-display text-xl font-bold mt-1">Product</div>
              </div>
              <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-black"><X size={20} /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 p-6">
              <Field label="Name" required full>
                <input className="input-stark" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="product-name-input" />
              </Field>
              <Field label="Kind">
                <select className="input-stark" value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                  <option value="equipment">Equipment</option>
                  <option value="service">Service</option>
                </select>
              </Field>
              <Field label="Category">
                <select className="input-stark" value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Typical price (USD)">
                <input className="input-stark" type="number" value={form.typical_price_usd || 0}
                  onChange={(e) => setForm({ ...form, typical_price_usd: e.target.value })} />
              </Field>
              <Field label="Applications (comma-separated)" full>
                <input className="input-stark" value={appsText}
                  onChange={(e) => setAppsText(e.target.value)} placeholder="cement, water_treatment" />
              </Field>
              <Field label="Description" full>
                <textarea className="input-stark min-h-[100px]" value={form.description || ""}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </Field>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn-primary" data-testid="save-product-button">
                {editing ? "Save changes" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
